import { useState, useEffect } from 'react';
import { ClaudeAPIService } from '../services/ClaudeAPIService';

interface APIKeyConfigProps {
  onApiKeySet?: (isConfigured: boolean) => void;
}

export function APIKeyConfig({ onApiKeySet }: APIKeyConfigProps) {
  const [apiKey, setApiKey] = useState('');
  const [isVisible, setIsVisible] = useState(false);
  const [status, setStatus] = useState('');
  const [isConfigured, setIsConfigured] = useState(false);

  const claudeService = ClaudeAPIService.getInstance();

  useEffect(() => {
    const configured = claudeService.isApiConfigured();
    setIsConfigured(configured);
    setStatus(claudeService.getApiStatus());
    onApiKeySet?.(configured);
  }, [onApiKeySet, claudeService]);

  const handleApiKeySubmit = async () => {
    if (!apiKey.trim()) {
      setStatus('Please enter a valid API key');
      return;
    }

    if (!apiKey.startsWith('sk-ant-')) {
      setStatus('Claude API keys should start with "sk-ant-"');
      return;
    }

    setStatus('Connecting to Claude API...');

    try {
      claudeService.setApiKey(apiKey);
      const configured = claudeService.isApiConfigured();
      setIsConfigured(configured);
      
      if (configured) {
        setStatus('✅ Successfully connected to Claude API!');
        onApiKeySet?.(configured);
        setApiKey(''); // Clear the input for security
        
        // Hide the config panel after a brief success message
        setTimeout(() => {
          setIsVisible(false);
        }, 1500);
      } else {
        setStatus('❌ Failed to connect to Claude API. Please check your API key.');
      }
    } catch (error) {
      console.error('API key configuration error:', error);
      setStatus('❌ Failed to configure API key. Please try again.');
    }
  };

  const handleGetApiKey = () => {
    window.open('https://console.anthropic.com/settings/keys', '_blank');
  };

  if (isConfigured) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
            <span className="text-sm font-medium text-green-800">
              🤖 Claude API Connected - Real AI Analysis Active
            </span>
          </div>
          <button
            onClick={() => setIsVisible(!isVisible)}
            className="text-xs text-green-600 hover:text-green-800"
          >
            Change API Key
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
      <div className="flex items-start space-x-3">
        <div className="flex-shrink-0">
          <svg className="w-5 h-5 text-yellow-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <div className="flex-1">
          <h4 className="text-sm font-medium text-yellow-800 mb-2">
            Get REAL Claude AI Analysis
          </h4>
          <p className="text-sm text-yellow-700 mb-4">
            Currently using intelligent simulation. For authentic Claude analysis, add your Anthropic API key to get:
          </p>
          <ul className="text-sm text-yellow-700 mb-4 space-y-1">
            <li>• 🤖 Real Claude Sonnet API analysis</li>
            <li>• 🧠 Genuine AI reasoning and insights</li>
            <li>• 📊 Advanced content understanding</li>
            <li>• 🎯 Claude's actual optimization recommendations</li>
          </ul>
          
          <div className="flex items-center space-x-2 mb-4">
            <button
              onClick={handleGetApiKey}
              className="text-sm bg-yellow-600 hover:bg-yellow-700 text-white px-3 py-1.5 rounded-md transition-colors"
            >
              Get Free API Key ($5 Credit)
            </button>
            <button
              onClick={() => setIsVisible(!isVisible)}
              className="text-sm bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-md transition-colors"
            >
              Configure API Key
            </button>
          </div>

          {isVisible && (
            <div className="space-y-3 pt-3 border-t border-yellow-300">
              <div>
                <label className="block text-sm font-medium text-yellow-800 mb-1">
                  Anthropic API Key
                </label>
                <div className="flex space-x-2">
                  <input
                    type="password"
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                    placeholder="sk-ant-..."
                    className="flex-1 px-3 py-2 border border-yellow-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  />
                  <button
                    onClick={handleApiKeySubmit}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md transition-colors"
                  >
                    Connect
                  </button>
                </div>
              </div>
              
              {status && (
                <div className={`text-sm p-2 rounded ${
                  status.includes('Connected') 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-red-100 text-red-800'
                }`}>
                  {status}
                </div>
              )}

              <div className="text-xs text-yellow-600">
                <p className="mb-1">🔒 Your API key is stored locally and never sent to our servers.</p>
                <p>💡 Get $5 free credits when you verify your Anthropic account with a phone number.</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}